import { Slider, Tooltip } from 'antd';
import InputField from '../Input/Input';
import styles from './ROICalculator.module.scss'; //roiOutputBgImage
import { useEffect, useState } from 'react';
import {
  CurrencyType,
  themeColor,
  SubscriptionType,
  MAX_NO_OF_PROCESS,
  MAX_USER_COST_INR,
  MAX_USER_COST_USD,
  simpleProcessInstructions,
  mediumProcessInstructions,
  complexProcessInstructions,
} from '../../constants/app.constants';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getPriceINRToUSD, getPriceUSDtoINR, priceConversion } from '../../utils/helpers/global';
import { useSelector } from 'react-redux';
const secondYearEmployeeCostIncrement = 1.1; // 10% increase in employee cost from second year onwards

const ROICalculator = ({ configuration }) => {
  const { priceDetails } = useSelector((state) => state.priceDetails);
  const effectivePriceDetails = configuration || priceDetails;
  const [currency, setCurrency] = useState(CurrencyType.INR);
  const [maxUserCostHourly, setMaxUserCostHourly] = useState(MAX_USER_COST_INR);
  const [noOfProcessRequired, setNoOfProcessRequired] = useState(1);
  const [hourType, setHourType] = useState('day');
  const [noOfManRequired, setNoOfManRequired] = useState(1);
  const [noOfHoursPerDay, setNoOfHoursPerDay] = useState(8);
  const [employeeCost, setEmployeeCost] = useState(500);
  const [noOfSimpleProcess, setNoOfSimpleProcess] = useState(0);
  const [noOfMediumProcess, setNoOfMediumProcess] = useState(0);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(
    SubscriptionType.monthly
  );
  const [calculatedValues, setCalculatedValues] = useState({
    minimumCostOfProcesses: 0,
    maximumCostOfProcesses: 0,
    subSubscriptionFee: 0,
    minimumROIPercentageFirstYear: 0,
    maximumROIPercentageFirstYear: 0,
    roiPercentageSecondYearOnwards: 0,
    agentCost: 0,
    controlRoomCost: 0,
    savedHours: 0,
  });
  const onChangesProcess = (value) => {
    if (value === '') {
      setNoOfProcessRequired('');
      setNoOfMediumProcess(0);
      setNoOfSimpleProcess(0);
      return;
    }
    const processInputRegex = /^(?:[1-9]|[1-9]\d|1\d\d|200)$/;
    if (!processInputRegex.test(value)) {
      return;
    }
    if (Number(value) > 0 && Number(value) <= MAX_NO_OF_PROCESS) {
      if (noOfSimpleProcess + noOfMediumProcess > Number(value)) {
        if (noOfMediumProcess > 0 && noOfSimpleProcess < Number(value)) {
          setNoOfMediumProcess(Number(value) - noOfSimpleProcess);
        } else {
          setNoOfMediumProcess(0);
          setNoOfSimpleProcess(Math.min(Number(value), noOfSimpleProcess));
        }
      }
      setNoOfProcessRequired(Number(value));
    }
  };
  const onChangesHours = (event) => {
    let value = event.target.value;
    if (value === '') {
      setNoOfHoursPerDay('');
      return;
    }
    value = value.replace(/^0+(?=\d)/, ''); // remove leading zeros
    event.target.value = value;
    const intValue = parseInt(value, 10);
    if (intValue >= 1) {
      setNoOfHoursPerDay(intValue);
    }
  };

  const onChangesEmployCost = (event) => {
    let value = event.target.value;
    if (value === '') {
      setEmployeeCost('');
      return;
    }
    const rupeeRegex = /^[1-9]\d*(\.\d{0,2})?$|^[1-9]\d*\.$/;
    if (!rupeeRegex.test(value)) {
      return;
    }
    if (Number(value) >= 0 && Number(value) <= maxUserCostHourly) {
      setEmployeeCost(value);
    }
  };

  const onChangesSimpleProcessCount = (event) => {
    let value = event.target.value;
    if (value === '') {
      setNoOfSimpleProcess('');
      return;
    }
    value = value.replace(/^0+(?=\d)/, ''); // remove leading zeros
    event.target.value = value;
    if (Number(value) >= 0 && Number(value) + noOfMediumProcess <= noOfProcessRequired) {
      setNoOfSimpleProcess(Number(value));
    }
  };

  const onChangesMediumProcessCount = (event) => {
    let value = event.target.value;
    if (value === '') {
      setNoOfMediumProcess('');
      return;
    }
    value = value.replace(/^0+(?=\d)/, ''); // remove leading zeros
    event.target.value = value;
    if (Number(value) >= 0 && noOfSimpleProcess + Number(value) <= noOfProcessRequired) {
      setNoOfMediumProcess(Number(value));
    }
  };

  const preventNonNumericAndDecimal = (e) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (employeeCost == '' || noOfHoursPerDay == '' || noOfProcessRequired == '') {
      return;
    }
    const {
      agentUptoTenMonthlyPrice,
      agentAboveTenMonthlyPrice,
      agentUptoTenAnnuallyPrice,
      agentAboveTenAnnuallyPrice,
      controlRoomMonthlyPrice,
      controlRoomAnnuallyPrice,
      simpleProcessMakingMinimumPrice,
      simpleProcessMakingMaximumPrice,
      mediumProcessMakingMinimumPrice,
      mediumProcessMakingMaximumPrice,
      complexProcessMakingMinimumPrice,
      complexProcessMakingMaximumPrice,
      noOfWorkingDaysInAMonth,
    } = effectivePriceDetails;
    const empCostInINR = currency === CurrencyType.USD ? getPriceUSDtoINR(employeeCost) : employeeCost;
    const increasedEmpCostInINR = empCostInINR * secondYearEmployeeCostIncrement;
    const hoursPerMonth = hourType === 'day' ? noOfHoursPerDay * noOfWorkingDaysInAMonth : noOfHoursPerDay;
    const firstYearTotalEmployeeCostPerMonth = empCostInINR * hoursPerMonth * noOfManRequired;
    const secondYearTotalEmployeeCostPerMonth = increasedEmpCostInINR * hoursPerMonth * noOfManRequired;
    console.log(firstYearTotalEmployeeCostPerMonth, secondYearTotalEmployeeCostPerMonth);
    const complexProcessCount = noOfProcessRequired - (noOfSimpleProcess + noOfMediumProcess);
    const minimumCostOfProcesses = simpleProcessMakingMinimumPrice * noOfSimpleProcess + mediumProcessMakingMinimumPrice * noOfMediumProcess +  complexProcessMakingMinimumPrice * complexProcessCount;
    const maximumCostOfProcesses = simpleProcessMakingMaximumPrice * noOfSimpleProcess + mediumProcessMakingMaximumPrice * noOfMediumProcess + complexProcessMakingMaximumPrice * complexProcessCount;

    let minimumROIPercentageFirstYear = 0;
    let maximumROIPercentageFirstYear = 0;
    let roiPercentageSecondYearOnwards = 0;
    let subSubscriptionFee = 0;
    let agentCost = 0;

    if (selectedSubscriptionType === SubscriptionType.monthly) {
      if (noOfProcessRequired <= 10) {
        agentCost = (agentUptoTenMonthlyPrice * noOfProcessRequired) ;
      } else {
        agentCost = ( 10 * agentUptoTenMonthlyPrice) + ((noOfProcessRequired - 10) * agentAboveTenMonthlyPrice) ;
      }
      subSubscriptionFee = agentCost + controlRoomMonthlyPrice;
      minimumROIPercentageFirstYear = ((firstYearTotalEmployeeCostPerMonth - (subSubscriptionFee + maximumCostOfProcesses/12 )) / (subSubscriptionFee + maximumCostOfProcesses/12 )) * 100;
      maximumROIPercentageFirstYear = ((firstYearTotalEmployeeCostPerMonth - (subSubscriptionFee + minimumCostOfProcesses/12 )) / (subSubscriptionFee + minimumCostOfProcesses/12 )) * 100;
      roiPercentageSecondYearOnwards = ((secondYearTotalEmployeeCostPerMonth - subSubscriptionFee ) / subSubscriptionFee ) * 100;
    } else {
      if (noOfProcessRequired <= 10) {
        agentCost = (agentUptoTenAnnuallyPrice * noOfProcessRequired);
      } else {
        agentCost = (10 * agentUptoTenAnnuallyPrice) + ((noOfProcessRequired - 10) * agentAboveTenAnnuallyPrice);
      }
      subSubscriptionFee = agentCost + controlRoomAnnuallyPrice;
      maximumROIPercentageFirstYear = ((firstYearTotalEmployeeCostPerMonth * 12 - (subSubscriptionFee + minimumCostOfProcesses )) / (subSubscriptionFee + minimumCostOfProcesses )) * 100;
      minimumROIPercentageFirstYear = ((firstYearTotalEmployeeCostPerMonth * 12 - (subSubscriptionFee + maximumCostOfProcesses )) / (subSubscriptionFee + maximumCostOfProcesses )) * 100;
      roiPercentageSecondYearOnwards = (((secondYearTotalEmployeeCostPerMonth * 12) - subSubscriptionFee ) / subSubscriptionFee ) * 100;
    }

    setCalculatedValues({
      minimumCostOfProcesses: minimumCostOfProcesses.toFixed(2),
      maximumCostOfProcesses: maximumCostOfProcesses.toFixed(2),
      subSubscriptionFee: subSubscriptionFee.toFixed(2),
      minimumROIPercentageFirstYear: minimumROIPercentageFirstYear.toFixed(2),
      maximumROIPercentageFirstYear: maximumROIPercentageFirstYear.toFixed(2),
      roiPercentageSecondYearOnwards: roiPercentageSecondYearOnwards.toFixed(2),
      agentCost: agentCost.toFixed(2),
      controlRoomCost: selectedSubscriptionType === SubscriptionType.monthly ? controlRoomMonthlyPrice.toFixed(2) : controlRoomAnnuallyPrice.toFixed(2),
      savedHours: selectedSubscriptionType === SubscriptionType.monthly ? hoursPerMonth : hoursPerMonth * 12,
    });
  }, [
    employeeCost,
    noOfProcessRequired,
    noOfHoursPerDay,
    noOfSimpleProcess,
    noOfMediumProcess,
    effectivePriceDetails,
    selectedSubscriptionType,
    currency,
    hourType,
    noOfManRequired,
  ]);

  return (
    <div className={styles.roiCalculatorContainer}>
      <div className={styles.roiInputsContainer}>
        <div className={styles.processInputContainer}>
          <div className={styles.noOfProcessInputContainer}>
            <label className={styles.roiInputLabels}>
              Number of business processes to automate
            </label>
            <div className={styles.processInputInfoContainer}>
              <InputField
                className={styles.roiInputField}
                type="number"
                min={1}
                max={MAX_NO_OF_PROCESS}
                value={noOfProcessRequired}
                onChange={(event) => {
                  let value = event.target.value;
                  value = value.replace(/^0+(?=\d)/, '');
                  event.target.value = value;
                  onChangesProcess(value);
                }}
                onBlur={() => {
                  if (noOfProcessRequired === '') {
                    setNoOfProcessRequired(1);
                  }
                }}
                onKeyDown={preventNonNumericAndDecimal}
              />
              <Tooltip
                title={'An agent is required for each bot!'}
                arrowPointAtCenter
                overlayInnerStyle={{
                  background: themeColor.toolTipColor,
                  fontSize: '11px',
                  color: themeColor.toolTipTextColor,
                }}
              >
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={styles.sliderContainer}>
            <span>1</span>
            <Slider
              max={MAX_NO_OF_PROCESS}
              min={1}
              step={1}
              value={noOfProcessRequired}
              onChange={onChangesProcess}
              error={true}
              className={styles.slider}
            />
            <span>{MAX_NO_OF_PROCESS}</span>
          </div>
        </div>
        <div className={styles.processTypeFieldsContainer}>
          <span className={styles.roiInputLabels}>
            Task Type Count
            <Tooltip
              title={
                'The no of complex processes will be calculated based on total processes minus simple and medium processes!'
              }
              arrowPointAtCenter
              overlayInnerStyle={{
                background: themeColor.toolTipColor,
                fontSize: '11px',
                color: themeColor.toolTipTextColor,
              }}
            >
              <InfoCircleOutlined className={styles.infoIcon} style={{ marginLeft: '1rem' }} />
            </Tooltip>
          </span>
          <div className={styles.processTypeFields}>
            <div className={styles.processTypeFieldContainer}>
              <label className={styles.taskTypeLabel}>Simple</label>
              <div className={styles.processTypeInputInfoContainer}>
                <InputField
                  className={styles.roiInputField}
                  name="simple"
                  type="number"
                  min={0}
                  max={MAX_NO_OF_PROCESS}
                  value={noOfSimpleProcess}
                  onChange={onChangesSimpleProcessCount}
                  onKeyDown={preventNonNumericAndDecimal}
                  onBlur={(event) => {
                    if (event.target.value == '') {
                      setNoOfSimpleProcess(0);
                    }
                  }}
                  precision={0}
                />
                <Tooltip
                  title={simpleProcessInstructions}
                  arrowPointAtCenter
                  placement="top"
                  overlayInnerStyle={{
                    background: themeColor.toolTipColor,
                    fontSize: '11px',
                    color: themeColor.toolTipTextColor,
                  }}
                >
                  <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
              </div>
            </div>
            <div className={styles.processTypeFieldContainer}>
              <label className={styles.taskTypeLabel}>Medium</label>
              <div className={styles.processTypeInputInfoContainer}>
                <InputField
                  className={styles.roiInputField}
                  name="medium"
                  type="number"
                  min={0}
                  max={MAX_NO_OF_PROCESS}
                  value={noOfMediumProcess}
                  onChange={onChangesMediumProcessCount}
                  onKeyDown={preventNonNumericAndDecimal}
                  onBlur={(event) => {
                    if (event.target.value == '') {
                      setNoOfMediumProcess(0);
                    }
                  }}
                  precision={0}
                />
                <Tooltip
                  title={mediumProcessInstructions}
                  arrowPointAtCenter
                  placement="top"
                  overlayInnerStyle={{
                    background: themeColor.toolTipColor,
                    fontSize: '11px',
                    color: themeColor.toolTipTextColor,
                  }}
                >
                  <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
              </div>
            </div>
            <div className={styles.processTypeFieldContainer}>
              <label className={styles.taskTypeLabel}>Complex</label>
              <div className={styles.processTypeInputInfoContainer}>
                <InputField
                  className={styles.roiInputField}
                  name="complex"
                  type="number"
                  min={0}
                  disabled
                  max={MAX_NO_OF_PROCESS}
                  value={
                    noOfProcessRequired != ''
                      ? noOfProcessRequired - (noOfSimpleProcess + noOfMediumProcess)
                      : 0
                  }
                  onKeyDown={preventNonNumericAndDecimal}
                  precision={0}
                />
                <Tooltip
                  title={complexProcessInstructions}
                  arrowPointAtCenter
                  placement="top"
                  overlayInnerStyle={{
                    background: themeColor.toolTipColor,
                    fontSize: '11px',
                    color: themeColor.toolTipTextColor,
                  }}
                >
                  <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.manHourSection}>

  {/* Row 1 */}
  <div className={styles.manHourRow}>

    <span className={styles.roiInputLabels}>
      No of hours per man
    </span>

    <div className={styles.toggleContainer}>

      <button
        className={`${styles.toggleButton} ${
          hourType === 'day' ? styles.activeToggle : ''
        }`}
        onClick={() => setHourType('day')}
      >
        Per Day
      </button>

      <button
        className={`${styles.toggleButton} ${
          hourType === 'month' ? styles.activeToggle : ''
        }`}
        onClick={() => setHourType('month')}
      >
        Per Month
      </button>

    </div>

    <InputField
      className={styles.roiInputField}
      type="number"
      min={1}
      value={noOfHoursPerDay}
      onChange={onChangesHours}
      onKeyDown={preventNonNumericAndDecimal}
    />

  </div>

  {/* Row 2 */}

  <div className={styles.manHourRow}>

    <span className={styles.roiInputLabels}>
      No of man require
    </span>

    <InputField
      className={styles.roiInputField}
      type="number"
      min={1}
      value={noOfManRequired}
      onChange={(e) => setNoOfManRequired(Number(e.target.value))}
      onKeyDown={preventNonNumericAndDecimal}
    />

  </div>
  <div className={styles.employeeCostSection}>
  <div className={styles.currencyContainer}>
            <label className={styles.roiInputLabels}>Currency</label>
            <div className={styles.currencyRadioButtons}>
              <span className={styles.currencyLabel}> {CurrencyType.INR} </span>
              <input
                type="radio"
                value={CurrencyType.INR}
                checked={currency === CurrencyType.INR}
                onChange={(event) => {
                  const inrPrice = getPriceUSDtoINR(employeeCost);
                  if (inrPrice > MAX_USER_COST_INR) {
                    setEmployeeCost(MAX_USER_COST_INR);
                  } else {
                    setEmployeeCost(inrPrice);
                  }
                  setMaxUserCostHourly(MAX_USER_COST_INR);
                  setCurrency(event.target.value);
                }}
                className={styles.currencyRadioButton}
              />
              <span className={styles.currencyLabel}> {CurrencyType.USD} </span>
              <input
                type="radio"
                value={CurrencyType.USD}
                checked={currency === CurrencyType.USD}
                onChange={(event) => {
                  const usdPrice = getPriceINRToUSD(employeeCost);
                  if (usdPrice > MAX_USER_COST_USD) {
                    setEmployeeCost(MAX_USER_COST_USD);
                  } else {
                    setEmployeeCost(usdPrice);
                  }
                  setMaxUserCostHourly(MAX_USER_COST_USD);
                  setCurrency(event.target.value);
                }}
              />
            </div>
    </div> 

  <div className={styles.manHourRow}>         
    <span className={styles.roiInputLabels}>
      Per man hour cost
    </span>
    <InputField
      className={styles.roiInputField}
      type="number"
      value={employeeCost}
      onChange={onChangesEmployCost}
      onKeyDown={(e) => {
        if (['e', 'E', '+', '-'].includes(e.key)) {
          e.preventDefault();
        }
      }}
      prefix={currency === CurrencyType.USD ? '$' : '₹'}
    />

  </div>
  <div className={styles.processInputContainer}>
          <div className={styles.sliderContainer}>
            <span>{currency === CurrencyType.INR ? '₹' : '$'}1</span>
            <Slider
              max={maxUserCostHourly}
              min={1}
              step={1}
              className={styles.slider}
              value={employeeCost}
              onChange={(value) => {
                setEmployeeCost(Number(value));
              }}
            />
            <span>
              {currency === CurrencyType.INR ? '₹' : '$'}
              {maxUserCostHourly}
            </span>
          </div>                          
        </div>
        </div>

        </div>        
      </div>
      <div className={styles.roiOutputsContainer}>
        <p className={styles.heading}>ROI calculation</p>
        <div className={styles.recommendedCountsContainer}>
          <div className={styles.recommendedCounts}>
            <span className={styles.recommendedCountLabel}>Bots/Agents</span>
            <span className={styles.count}>
              {noOfProcessRequired == '' ? '--' : noOfProcessRequired}
            </span>
          </div>
          <div>
            <div className={styles.recommendedCounts}>
              <span className={styles.recommendedCountLabel}>
                Control Room Access/Environment
                <Tooltip
                  title={'Considering a single Control Room is required for the intranet network!'}
                  arrowPointAtCenter
                  overlayInnerStyle={{
                    background: themeColor.toolTipColor,
                    fontSize: '11px',
                    color: themeColor.toolTipTextColor,
                  }}
                >
                  <InfoCircleOutlined className={styles.infoIcon} style={{ color: 'white ', marginLeft: '0.5rem' }} />
                </Tooltip>
              </span>
              <span className={styles.count}>1</span>
            </div>
          </div>
        </div>
        <div className={styles.subscriptionTypeContainer}>
          <label className={styles.subscriptionTypeHeading}>Subscription Type:</label>
          <div className={styles.subscriptionTypeRadioButtonsContainer}>
            <div className={styles.subscriptionTypeRadioButton}>
              <input
                type="radio"
                value={SubscriptionType.monthly}
                checked={selectedSubscriptionType === SubscriptionType.monthly}
                onChange={(event) => {
                  setSelectedSubscriptionType(event.target.value);
                }}
                className={styles.currencyRadioButton}
              />
              <span className={styles.currencyLabel}> {'Monthly'} </span>
            </div>
            <div className={styles.subscriptionTypeRadioButton}>
              <input
                type="radio"
                value={SubscriptionType.annually}
                checked={selectedSubscriptionType === SubscriptionType.annually}
                onChange={(event) => {
                  setSelectedSubscriptionType(event.target.value);
                }}
              />
              <span className={styles.currencyLabel}> {'Annually'} </span>
            </div>
          </div>
        </div>
        
        <div className={styles.dividerLine} />

        <div className={styles.onTimeProcessCostContainer}> 
          <p className={styles.onTimeProcessCostHeading}>One time process implementation cost</p>
          <div className={styles.onTimeProcessCosts}>
            <span className={styles.onTimeProcessCostValue}>
              {currency === CurrencyType.INR ? '₹ ' : '~ $'}{ priceConversion(calculatedValues.minimumCostOfProcesses, currency) || '--.--'}
            </span>
            <span className={styles.onTimeProcessCostValue}>to</span>
            <span className={styles.onTimeProcessCostValue}>
              {currency === CurrencyType.INR ? '₹ ' : '~ $'}{ priceConversion(calculatedValues.maximumCostOfProcesses, currency) || '--.--'}
            </span>
          </div>
        </div>

        <div className={styles.dividerLine} />

        <div className={styles.roiSubscriptionSection}>
            <p className={styles.calculatedResultSectionHeading}>Subscription fee</p>
            <div className={styles.subscriptionFee}>
              <div className={`${styles.calculatedFee}`}>
                <span className={styles.countLabel}>Agent Cost </span>
                <span className={styles.count}>
                  {currency === CurrencyType.INR ? '₹ ' : '~ $'}
                  {noOfProcessRequired == ''
                    ? '--.--'
                    : priceConversion(calculatedValues.agentCost, currency)}
                </span>
              </div>
              <div className={`${styles.calculatedFee}`}>
                <span className={styles.countLabel}>Control Room Cost </span>
                <span className={styles.count}>
                  {currency === CurrencyType.INR ? '₹ ' : '~ $'}
                  {noOfProcessRequired == ''
                    ? '--.--'
                    : priceConversion(calculatedValues.controlRoomCost, currency)}
                </span>
              </div>
              <div className={`${styles.calculatedFee}`}>
                <span className={styles.totalLabel}>Total Cost </span>
                <span className={styles.totalCount}>
                  {currency === CurrencyType.INR ? '₹ ' : '~ $'}
                  {noOfProcessRequired == ''
                    ? '--.--'
                    : priceConversion(calculatedValues.subSubscriptionFee, currency)}
                </span>
              </div>
            </div>
          </div>
        <div className={styles.dividerLine} />

        <div className={styles.roiCalculationSection}>
          <div className={styles.calculatedResultSection}>
            <p className={styles.calculatedResultSectionHeading}>First year</p>
              <div className={styles.firstYearROIContainer}>
                <div className={styles.roiPercentageContainer}>
                  <span>ROI:</span>
                  <span className={styles.roiValue}>{`${calculatedValues.minimumROIPercentageFirstYear}% to ${calculatedValues.maximumROIPercentageFirstYear}%`}</span>
                </div>
              </div>
          </div>
          <div className={styles.calculatedResultSection}>
            <p className={styles.calculatedResultSectionHeading}>Second year onwards</p>
            <div className={styles.roiPercentageContainer}>
              <span>ROI:</span>
              <span className={styles.roiValue}>
                {calculatedValues.roiPercentageSecondYearOnwards}%
              </span>
            </div>
            <div className={styles.savedHoursContainer}>
              <span className={styles.savedHoursMessage}>{`Man hours saved per ${selectedSubscriptionType === SubscriptionType.monthly ? 'month' : 'year'} : `}</span>
              <span className={styles.savedHours}>{calculatedValues.savedHours}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ROICalculator;
